<script setup>
import { computed, inject, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import DigestPreview from '@/components/landing/DigestPreview.vue';
import TabCollapseDemo from '@/components/landing/TabCollapseDemo.vue';
import { redirectToLogin, resolveLoginRedirectTarget } from '@/config/auth';
import { resolveSchoolId, schoolOptions, syncActiveSchool } from '@/config/schools';

const route = useRoute();
const router = useRouter();
const theme = inject('theme', ref('dark'));
const setTheme = inject('setTheme', () => {});
const isDark = computed(() => theme.value === 'dark');
const selectedSchoolId = ref(resolveSchoolId({ fallbackToDefault: false }) || '');
const isGrace = computed(() => selectedSchoolId.value === 'grace');
const chooser = ref(null);
const preview = ref(null);
const loginError = ref('');
const assignments = ref([
  { title: 'Cell division lab report', course: 'Biology', due: 'Tomorrow', done: true },
  { title: 'Read chapters 6–8', course: 'English 11', due: 'Tomorrow', done: false },
  { title: 'Chapter 7 problem set', course: 'Algebra 2', due: 'Wednesday', done: false },
]);
const completed = computed(() => assignments.value.filter(item => item.done).length);
const exampleScore = ref(90);
const exampleAverage = computed(() => ((86 * 4 + Number(exampleScore.value)) / 5).toFixed(1));

function showPreview() {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  preview.value?.scrollIntoView({ behavior: reducedMotion ? 'instant' : 'smooth', block: 'start' });
  preview.value?.focus({ preventScroll: true });
}

function openSchoolChooser() {
  chooser.value?.showModal();
}

function selectSchoolAndLogin(schoolId) {
  selectedSchoolId.value = schoolId;
  loginError.value = '';
  syncActiveSchool({ schoolId });
  redirectToLogin({
    originalUrl: resolveLoginRedirectTarget(route.query.loginRedirect),
    schoolId,
  });
}

onMounted(() => {
  if (selectedSchoolId.value) syncActiveSchool({ schoolId: selectedSchoolId.value });
  if (route.query.loginError === 'wrong_school') {
    loginError.value = 'That account belongs to a different school. Choose the other school to continue.';
    openSchoolChooser();
    const query = { ...route.query };
    delete query.loginError;
    router.replace({ path: route.path, query, hash: route.hash });
  }
});
</script>

<template>
  <div class="welcome" :data-theme="isDark ? 'dark' : 'light'">
    <div class="appearance">
      <button class="theme-toggle" @click="setTheme(isDark ? 'light' : 'dark')" :aria-label="isDark ? 'Switch to light mode' : 'Switch to dark mode'">
        <svg v-if="isDark" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><circle cx="12" cy="12" r="4"/><path d="M12 2v2m0 16v2M2 12h2m16 0h2M5 5l1.5 1.5m11 11L19 19M5 19l1.5-1.5m11-11L19 5"/></svg>
        <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M20 15.5A8.5 8.5 0 0 1 8.5 4 8.5 8.5 0 1 0 20 15.5Z"/></svg>
      </button>
    </div>

    <main>
    <section class="welcome-layout" aria-labelledby="welcome-title">
      <div class="brand">
        <img v-if="isGrace" class="grace-logo" src="/GraceCrest-TextRight-294U.png" alt="Grace Church School" />
        <img v-else-if="isDark" class="school-logo dark-logo" src="/branding/collegiate-dark.png" alt="Collegiate School" />
        <img v-else class="school-logo light-logo" src="/branding/collegiate-light.jpg" alt="Collegiate School" />
        <span>BetterConnect</span>
      </div>

      <!-- Decorative sample content, not the signed-out visitor's actual school data. -->
      <div class="illustration" aria-hidden="true">
        <div class="purple-shape"></div>
        <div class="schedule-card">
          <div class="schedule-heading"><b>Monday’s schedule</b><span>Nov 2</span></div>
          <div class="lesson"><span class="time">9:45</span><div class="course"><b>Biology</b><small>Room 204 · Ms. Chen</small></div></div>
          <div class="lesson"><span class="time">10:40</span><div class="course english"><b>English 11</b><small>Room 108 · Mr. Davis</small></div></div>
        </div>
        <div class="assignment-card">
          <div class="assignment-heading"><span>Assignments</span><span>1 of 3 done</span></div>
          <div class="done-row"><span class="check"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2"><path d="m3 8 3 3 7-7"/></svg></span><div><b>Cell division lab report</b><small>Biology · All set for tomorrow</small></div></div>
        </div>
        <div class="menu-pill"><svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M4 2v5m3-5v5M2 2v4a3 3 0 0 0 6 0V2M5 9v9M15 2c-3 3-3 7 0 8h2V2h-2Zm2 8v8"/></svg><div><small>Lunch today</small><b>Chicken parmesan</b></div></div>
      </div>

      <section class="welcome-copy" aria-labelledby="welcome-title">
        <h1 id="welcome-title">Your school<br />day, sorted.</h1>
        <p class="lede">What’s next, what’s due, and how you’re doing. All together in BetterConnect.</p>
        <button class="primary" @click="openSchoolChooser">Continue with Blackbaud</button>
        <button class="secondary" @click="showPreview">Try a demo first</button>
        <p class="privacy-note"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.3" aria-hidden="true"><rect x="3" y="7" width="10" height="7" rx="2"/><path d="M5 7V5a3 3 0 0 1 6 0v2"/></svg>Secure sign-in with Blackbaud</p>
      </section>
    </section>

    <section class="tab-demo-section content-width" aria-label="From six tabs to one">
      <TabCollapseDemo />
    </section>

    <section id="preview" ref="preview" class="product-story content-width" tabindex="-1" aria-labelledby="preview-title">
      <div class="story-heading">
        <h2 id="preview-title">Connect has the information.<br />Make the most of it.</h2>
      </div>
      <p class="sample-note section-sample">Try the examples below. All data shown is sample data.</p>
      <div class="feature-pair">
        <article class="feature-tile assignments-tile">
          <div class="example-copy"><h3>The whole week.<br />Off your mind.</h3><p>See assignments by day or week, track what you’ve finished, and see how many classmates have completed each one.</p></div>
          <div class="assignment-example example-surface">
            <div class="example-topline"><b>Due soon</b><span aria-live="polite">{{ completed }} of 3 done</span></div>
            <label v-for="(assignment, index) in assignments" :key="assignment.title" class="demo-assignment" :class="{ completed: assignment.done }">
              <input type="checkbox" v-model="assignment.done" /><span><b>{{ assignment.title }}</b><small>{{ assignment.course }} · {{ assignment.due }}</small><small class="class-progress">{{ [15, 8, 4][index] }} classmates completed</small></span>
            </label>
            <p class="example-instruction">Check one off. Your list gets a little lighter.</p>
          </div>
        </article>
        <article class="feature-tile grades-tile">
          <div class="example-copy"><h3>“What if I get a 90?”<br />Find out.</h3><p>Try a future test score and see how your average could change. Your teachers’ posted grades stay exactly as they are.</p></div>
          <div class="grade-example example-surface">
            <div class="example-topline"><b>Algebra 2</b><span>What-if calculator</span></div>
            <div class="grade-comparison">
              <div><span>Current average</span><strong>86<span>%</span></strong></div>
              <span class="grade-arrow" aria-hidden="true">→</span>
              <div class="estimated-grade"><span>What-if estimate</span><strong aria-live="polite">{{ exampleAverage }}<span>%</span></strong></div>
            </div>
            <label class="score-label" for="example-score">Your next test score <output for="example-score">{{ exampleScore }}%</output></label>
            <input id="example-score" v-model.number="exampleScore" type="range" min="0" max="100" step="1" />
            <p class="example-instruction">Move the slider to try a score.</p>
            <p class="calculation-note">Sample calculation: four equally weighted grades averaging 86%, plus one new test. Your course’s grading setup determines your actual estimate.</p>
          </div>
        </article>
      </div>

      <article class="digest-feature">
        <div class="example-copy"><h3>A heads-up.<br />Before the day<br />gets ahead of you.</h3><p>Assignments and your first class, delivered as a text. Get a morning digest, a night-before reminder, or both.</p><p class="digest-opt-in">Only if you want them. Add your number and opt into text digests in settings.</p></div>
        <DigestPreview />
      </article>

    </section>

    </main>

    <footer class="welcome-footer"><span>© {{ new Date().getFullYear() }} BetterConnect</span><nav aria-label="Legal"><RouterLink to="/privacy">Privacy</RouterLink><RouterLink to="/terms">Terms</RouterLink></nav></footer>

    <dialog ref="chooser" class="school-chooser" aria-labelledby="chooser-title" @click.self="chooser.close()">
      <div class="chooser-panel">
        <button class="chooser-close" @click="chooser.close()">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 18 18 6M6 6l12 12" /></svg>
          Close
        </button>
        <h2 id="chooser-title">Choose your school</h2>
        <p class="chooser-description">You’ll sign in with your school’s Blackbaud account.</p>
        <p v-if="loginError" class="login-error" role="alert">{{ loginError }}</p>
        <div class="school-cards">
          <button v-for="school in schoolOptions" :key="school.id" class="school-card" @click="selectSchoolAndLogin(school.id)">
            <span class="school-card-crest">
              <img v-if="school.id === 'collegiate'" src="/CS_Crest_Blue_PMS.png" alt="" class="collegiate-crest" />
              <img v-else src="/GraceCrest-TextRight-294U.png" alt="" class="grace-crest" />
            </span>
            <span class="school-card-name">{{ school.name }}</span>
          </button>
        </div>
      </div>
    </dialog>


  </div>
</template>

<style scoped>
.welcome {
  --base: #faf9fd;
  --ink: #22212c;
  --muted: #696676;
  --edge: #e3e0eb;
  --card: #fff;
  min-height: 100svh;
  display: flex;
  flex-direction: column;
  background: var(--base);
  color: var(--ink);
  font-family: -apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;
}

.welcome[data-theme=dark] {
  --base: #1c1c23;
  --ink: #f8f8fc;
  --muted: #a9a8b7;
  --edge: #373640;
  --card: #292932;
}

.welcome button,.welcome a,.welcome input {
  touch-action: manipulation;
}

.welcome button:focus-visible,.welcome a:focus-visible,.welcome input:focus-visible {
  outline: 3px solid #a7a5ff;
  outline-offset: 5px;
}

.appearance {
  display: flex;
  justify-content: flex-end;
  padding: 16px 24px 0;
}

.theme-toggle {
  color: var(--muted);
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
}

.theme-toggle:hover {
  background: var(--edge);
}

.theme-toggle svg {
  width: 21px;
  height: 21px;
}

.welcome-layout {
  width: 100%;
  max-width: 1060px;
  margin: 0 auto;
  min-height: 620px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto auto;
  column-gap: 80px;
  row-gap: 32px;
  padding: 48px 48px 64px;
  align-content: center;
  flex: 1;
}

.brand {
  grid-column: 2;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 18px;
  font-weight: 650;
  letter-spacing: -.4px;
}

.school-logo {
  width: 40px;
  height: 40px;
  object-fit: contain;
}

.dark-logo {
  mix-blend-mode: screen;
}

.light-logo {
  padding: 3px;
  background: #fff;
  border-radius: 8px;
}

.grace-logo {
  width: 84px;
  height: 40px;
  object-fit: contain;
  background: white;
  border-radius: 6px;
  padding: 4px;
}

.welcome-copy {
  grid-column: 2;
  grid-row: 2;
  max-width: 410px;
}

.welcome-copy h1 {
  font-size: clamp(44px,4.8vw,58px);
  font-weight: 750;
  letter-spacing: -2.4px;
  line-height: 1.08;
  margin: 0 0 22px;
}

.lede {
  color: var(--muted);
  font-size: 16px;
  line-height: 1.7;
  margin: 0 0 32px;
  max-width: 380px;
}

.primary {
  display: block;
  width: 100%;
  border: 0;
  background: #6759ed;
  color: white;
  border-radius: 13px;
  padding: 17px 18px;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
}

.primary:hover {
  background: #7669f4;
}

.primary:active {
  background: #594bd6;
}

.secondary {
  display: block;
  width: 100%;
  min-height: 48px;
  font-size: 14px;
  color: var(--muted);
  margin-top: 6px;
  padding: 10px;
}

.secondary:hover {
  color: var(--ink);
}

.privacy-note {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 7px;
  margin: 18px 0 0;
  color: var(--muted);
  font-size: 12px;
}

.privacy-note svg {
  width: 13px;
  height: 13px;
  flex-shrink: 0;
}

.illustration {
  grid-column: 1;
  grid-row: 1/3;
  align-self: center;
  position: relative;
  isolation: isolate;
  width: 100%;
  aspect-ratio: 320/340;
  font-size: clamp(12px,1.4vw,15px);
}

.purple-shape {
  position: absolute;
  inset: 0 5% 2% 5%;
  border-radius: 34% 34% 7% 7%;
  background: #5346cf;
  overflow: hidden;
}

.purple-shape:before,.purple-shape:after {
  content: "";
  position: absolute;
  border: 1px solid #ffffff20;
  width: 110%;
  aspect-ratio: 1;
  border-radius: 50%;
  left: -57%;
  top: 20%;
}

.purple-shape:after {
  left: -45%;
}

.schedule-card {
  position: absolute;
  left: 2%;
  top: 13%;
  width: 89%;
  padding: 6%;
  background: var(--card);
  border: 1px solid var(--edge);
  border-radius: 16px;
  transform: rotate(-5deg);
  box-shadow: 0 14px 24px #17112e20;
}

.schedule-heading {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 13px;
}

.schedule-heading b {
  font-size: .95em;
  font-weight: 600;
}

.schedule-heading span {
  font-size: .72em;
  color: var(--muted);
}

.lesson {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 13px 0;
  border-top: 1px solid var(--edge);
}

.time {
  font-size: .75em;
  color: var(--muted);
  font-variant-numeric: tabular-nums;
  width: 34px;
  padding-top: 2px;
}

.course {
  padding-left: 10px;
  border-left: 2px solid #aaa4ff;
}

.course b {
  display: block;
  font-size: .95em;
  font-weight: 600;
}

.course small {
  display: block;
  font-size: .73em;
  color: var(--muted);
  margin-top: 5px;
}

.course.english {
  border-color: #e1ae6d;
}

.assignment-card {
  position: absolute;
  top: 56%;
  left: 12%;
  width: 88%;
  background: #eeebff;
  color: #302955;
  transform: rotate(4deg);
  padding: 5%;
  border-radius: 14px;
  box-shadow: 0 12px 20px #17112e20;
}

.assignment-heading {
  display: flex;
  justify-content: space-between;
  font-size: .74em;
  color: #645885;
  margin-bottom: 14px;
  gap: 8px;
}

.done-row {
  display: flex;
  gap: 10px;
  align-items: center;
}

.check {
  display: grid;
  place-items: center;
  width: 25px;
  height: 25px;
  flex-shrink: 0;
  border-radius: 50%;
  background: #5c4bd3;
  color: white;
}

.check svg {
  width: 15px;
  height: 15px;
}

.done-row b {
  font-size: .9em;
  font-weight: 600;
  display: block;
}

.done-row small {
  display: block;
  font-size: .72em;
  margin-top: 5px;
  color: #766d8e;
}

.menu-pill {
  position: absolute;
  left: 7%;
  bottom: 4%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 17px;
  border-radius: 40px;
  background: #fff1db;
  color: #664726;
  border: 1px solid #ffdda6;
  transform: rotate(-2deg);
}

.menu-pill svg {
  width: 23px;
  height: 23px;
  flex-shrink: 0;
}

.menu-pill small {
  display: block;
  font-size: .72em;
  margin-bottom: 3px;
}

.menu-pill b {
  font-size: .84em;
  font-weight: 600;
  display: block;
}

.welcome-footer {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px 24px;
  padding: 20px 24px;
  font-size: 11px;
  color: var(--muted);
}

.welcome-footer nav {
  display: flex;
  gap: 18px;
}

.welcome-footer a:hover {
  text-decoration: underline;
}

.school-chooser {
  width: 100%;
  max-width: none;
  height: 100%;
  max-height: none;
  margin: 0;
  padding: 20px;
  border: 0;
  background: transparent;
  color: var(--ink);
}
.school-chooser[open] {
  display: grid;
  place-items: center;
  animation: chooser-fade 0.3s ease both;
}
.school-chooser::backdrop {
  background: color-mix(in srgb, var(--base) 94%, transparent);
  backdrop-filter: blur(20px);
}
.chooser-panel {
  position: relative;
  width: min(672px, 100%);
}
.chooser-close {
  position: absolute;
  top: -48px;
  right: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  color: var(--muted);
  font-size: 14px;
}
.chooser-close:hover {
  color: var(--ink);
}
.chooser-close svg {
  width: 16px;
  height: 16px;
}
.school-chooser h2 {
  font-size: clamp(28px, 3vw, 36px);
  font-weight: 650;
  line-height: 1.18;
  letter-spacing: -1.2px;
}
.chooser-description {
  margin: 8px 0 32px;
  color: var(--muted);
  font-size: 15px;
  line-height: 1.6;
}
.school-cards {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
.school-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  padding: 24px;
  border: 1px solid var(--edge);
  border-radius: 16px;
  background: #fff;
  color: #22212c;
  text-align: left;
  box-shadow: inset 0 -3px 0 transparent;
  transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.25s ease;
}
.school-chooser[open] .school-card {
  animation: card-rise 0.45s cubic-bezier(0.16, 1, 0.3, 1) both;
}
.school-chooser[open] .school-card:nth-child(2) {
  animation-delay: 0.08s;
}
.school-card:hover {
  transform: translateY(-3px);
  box-shadow: inset 0 -4px 0 #6759ed;
}
.school-card-crest {
  display: flex;
  align-items: center;
  height: 48px;
}
.collegiate-crest {
  height: 48px;
  object-fit: contain;
}
.grace-crest {
  height: 36px;
  object-fit: contain;
}
.school-card-name {
  font-size: 20px;
  font-weight: 650;
  letter-spacing: -0.3px;
}
@keyframes chooser-fade {
  from { opacity: 0; }
}
@keyframes card-rise {
  from { opacity: 0; transform: translateY(16px); }
}

.login-error {
  padding: 14px;
  background: #b91c1c18;
  color: #b91c1c;
  border-radius: 10px;
  font-size: 14px;
  margin-bottom: 24px;
}

.welcome[data-theme=dark] .login-error {
  color: #fca5a5;
  background: #ef444419;
}

.demo-assignment {
  display: flex;
  gap: 13px;
  padding: 15px 0;
  border-top: 1px solid var(--edge);
  cursor: pointer;
}

.demo-assignment input {
  width: 19px;
  height: 19px;
  accent-color: #6759ed;
  flex-shrink: 0;
  margin-top: 2px;
}

.demo-assignment b {
  font-size: 14px;
  display: block;
  font-weight: 550;
}

.demo-assignment small {
  font-size: 12px;
  color: var(--muted);
  display: block;
  margin-top: 5px;
}

.demo-assignment.completed b {
  text-decoration: line-through;
  color: var(--muted);
}

/* Product examples answer distinct questions instead of repeating the hero. */
.content-width {
  width: min(1060px, 100%);
  margin: 0 auto;
  padding-left: 48px;
  padding-right: 48px;
}
.tab-demo-section {
  padding-top: 56px;
  padding-bottom: 24px;
}
.product-story {
  padding-top: 72px;
  padding-bottom: 72px;
  scroll-margin-top: 24px;
}
.product-story:focus {
  outline: none;
}
.story-heading {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 40px;
  margin-bottom: 36px;
}
.story-heading h2 {
  font-size: clamp(28px, 3vw, 36px);
  font-weight: 650;
  line-height: 1.18;
  letter-spacing: -1.2px;
}
.example-copy h3 {
  font-size: 29px;
  font-weight: 650;
  line-height: 1.2;
  letter-spacing: -.8px;
  margin-bottom: 18px;
  max-width: 270px;
}
.example-copy > p {
  color: var(--muted);
  font-size: 14px;
  line-height: 1.8;
}
.sample-note {
  display: block;
  font-size: 11px;
  color: var(--muted);
  margin-top: 28px;
}
.example-surface {
  padding: 24px;
  border: 1px solid var(--edge);
  border-radius: 14px;
  background: var(--base);
  min-width: 0;
}
.example-topline {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
}
.example-topline b {
  font-size: 13px;
  font-weight: 600;
}
.example-topline > span {
  color: var(--muted);
  font-size: 10px;
}
.grade-comparison {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 6px 0 28px;
}
.grade-comparison > div > span {
  font-size: 11px;
  color: var(--muted);
}
.grade-comparison strong {
  display: block;
  font-size: 42px;
  font-weight: 600;
  letter-spacing: -1.5px;
  line-height: 1.3;
  font-variant-numeric: tabular-nums;
}
.grade-comparison strong span {
  font-size: 19px;
  padding-left: 2px;
}
.grade-arrow {
  color: var(--muted);
}
.estimated-grade strong {
  color: #6759ed;
}
.welcome[data-theme=dark] .estimated-grade strong {
  color: #b5aaff;
}
.score-label {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 12px;
}
.score-label output {
  font-weight: 600;
}
#example-score {
  display: block;
  width: 100%;
  accent-color: #8d7cf8;
  height: 32px;
  cursor: pointer;
  margin-top: 8px;
}
.example-instruction {
  font-size: 11px;
  color: var(--muted);
  margin-top: 9px;
}
.calculation-note {
  font-size: 10px;
  color: var(--muted);
  line-height: 1.7;
  margin-top: 22px;
}
/* Each feature stays visible; variation comes from the product examples. */
.section-sample {
  margin: -16px 0 28px;
}
.feature-pair {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}
.feature-tile {
  padding: 30px;
  border-radius: 22px;
  background: var(--card);
  display: flex;
  flex-direction: column;
  gap: 28px;
  min-width: 0;
}
.feature-tile .example-copy h3 {
  max-width: none;
}
.grades-tile {
  background: #ede9fc;
  --ink: #292337;
  --muted: #686074;
  --edge: #d8d0eb;
  --base: #f8f6ff;
  color: var(--ink);
}
.welcome[data-theme=dark] .grades-tile {
  background: #302b43;
  --ink: #f8f5ff;
  --muted: #bcb3d0;
  --edge: #49405f;
  --base: #24212f;
}
.class-progress {
  color: #5c8270 !important;
  font-size: 10px !important;
}
.welcome[data-theme=dark] .class-progress {
  color: #9ebdab !important;
}
.digest-feature {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: center;
  padding: 72px 32px;
}
.digest-feature .example-copy h3 {
  font-size: clamp(32px, 3.5vw, 44px);
  max-width: none;
  letter-spacing: -1.5px;
}
.digest-feature .example-copy > p {
  max-width: 320px;
  font-size: 16px;
}
.digest-feature .example-copy .digest-opt-in {
  font-size: 12px;
  margin-top: 24px;
  max-width: 290px;
}
@media (max-width:760px) {
  .feature-pair, .digest-feature {
    grid-template-columns: 1fr;
  }
  .feature-tile {
    padding: 24px 18px;
  }
  .digest-feature {
    gap: 32px;
    padding: 48px 0;
  }
  .digest-feature .example-copy > p {
    max-width: none;
  }
}

@media (max-width:760px) {
  .welcome-layout {
    min-height: auto;
  }
  .content-width {
    padding-left: 24px;
    padding-right: 24px;
  }
  .tab-demo-section {
    padding-top: 32px;
  }
  .product-story {
    padding-top: 44px;
    padding-bottom: 44px;
  }
  .story-heading {
    display: block;
    margin-bottom: 24px;
  }
  .example-copy h3 {
    max-width: none;
    font-size: 26px;
    margin-bottom: 12px;
  }
  .sample-note {
    margin-top: 14px;
  }
  .example-surface {
    padding: 18px;
  }
  .grade-comparison strong {
    font-size: 36px;
  }
  .grade-comparison > div > span {
    font-size: 10px;
  }
}

@media (max-width:760px) {
  .appearance {
    padding: 8px 16px 0;
  }
  .welcome-layout {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    max-width: 430px;
    padding: 0 28px 24px;
    gap: 0;
    flex: 0;
    margin: 0 auto;
  }
  .brand {
    font-size: 17px;
    margin-bottom: 22px;
  }
  .school-logo {
    width: 36px;
    height: 36px;
  }
  .illustration {
    width: 100%;
    max-width: 320px;
    font-size: 12px;
    align-self: center;
    margin: 0 0 28px;
    aspect-ratio: 320/340;
  }
  .welcome-copy {
    max-width: none;
  }
  .welcome-copy h1 {
    font-size: 44px;
    letter-spacing: -1.8px;
    margin-bottom: 16px;
  }
  .lede {
    font-size: 14px;
    line-height: 1.65;
    margin-bottom: 24px;
  }
  .primary {
    font-size: 15px;
    padding: 16px;
  }
  .privacy-note {
    font-size: 11px;
    margin-top: 14px;
  }
  .welcome-footer {
    margin-top: auto;
    font-size: 10px;
  }
  .schedule-card {
    border-radius: 13px;
  }
  .lesson {
    padding: 11px 0;
  }
  .assignment-heading {
    margin-bottom: 12px;
  }
  .menu-pill {
    padding: 10px 15px;
  }
  .menu-pill svg {
    width: 20px;
    height: 20px;
  }
}

@media (max-width:350px) {
  .welcome-layout {
    padding-left: 22px;
    padding-right: 22px;
  }
  .illustration {
    font-size: 11px;
  }
  .lesson {
    padding: 9px 0;
  }
  .schedule-heading {
    margin-bottom: 8px;
  }
  .assignment-heading {
    margin-bottom: 8px;
  }
  .menu-pill {
    padding: 8px 12px;
  }
  .welcome-copy h1 {
    font-size: 39px;
  }
  .school-cards {
    grid-template-columns: 1fr;
  }
}

@media (prefers-reduced-motion:reduce) {
  .welcome * {
    scroll-behavior: auto;
  }
}
</style>
